import { RouteConfig } from '../types';
import { ONBOARDING_KEYS } from '../constants';
import { HELP_CENTER_LINKS } from '../helpCenterLinks';

export const usersPageSteps = [
  {
    element: '[data-onboarding="users-page"]',
    popover: {
      title: 'Users Dashboard',
      description:
        `View all <a href="${HELP_CENTER_LINKS.users}" target="_blank" class="text-lime underline">users</a> in your system. Search, add users, and manage their access.`,
      side: 'bottom' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="users-add-button"]',
    popover: {
      title: 'Add New User',
      description:
        'Add a new user. Fill in their details and choose their access level.',
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="users-utility-bar"]',
    popover: {
      title: 'Search and Filter Tools',
      description:
        'Use the search bar to find users by name or email. Customize your view with column visibility to focus on the information you need.',
      side: 'bottom' as const,
      align: 'center' as const,
    },
  },
  {
    element: '[data-onboarding="users-table"] table tbody, [data-onboarding="users-table"] table',
    popover: {
      title: 'Users Table',
      description:
        'All users are listed with key details such as name, email, and roles. Click a user to view or edit their profile.',
      side: 'top' as const,
      align: 'start' as const,
    },
  },
];

export const addUserSteps = [
  {
    element: '[data-onboarding="add-user-header"]',
    popover: {
      title: 'Add New User',
      description:
        'Use this page to add a new user and set their access type that matches what they need to do.',
      side: 'bottom' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="add-user-basic-info"]',
    popover: {
      title: 'Basic Information',
      description:
        'The email is used for login and notifications, and the name helps identify them across the system.',
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="add-user-role-type"]',
    popover: {
      title: 'Access Type',
      description:
        'Choose an access type by selecting one of the cards. Each option gives a different level of permissions.',
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="add-user-role-monitoring"]',
    popover: {
      title: 'Monitoring Access',
      description:
        'Monitoring Access lets the user view and resolve alerts and use the monitoring dashboard. They can’t manage customers, sites, devices, or system settings.',
      side: 'top' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="add-user-role-admin"]',
    popover: {
      title: 'Admin Access',
      description:
        'Admin Access lets the user manage customers, sites, devices, connections, users, teams, and system settings. They can’t access monitoring features.',
      side: 'top' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="add-user-role-full"]',
    popover: {
      title: 'Full Access',
      description:
        'Full Access combines admin and monitoring. The user can manage configuration and also handle alerts and monitoring. This is the most powerful access level.',
      side: 'top' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="add-user-save-button"]',
    popover: {
      title: 'Save User',
      description:
        "Create the user with the details and access you've set.",
      side: 'top' as const,
      align: 'end' as const,
    },
  },
];

export const userDetailSteps = [
  {
    element: '[data-onboarding="user-detail-info-card"]',
    popover: {
      title: 'User Details',
      description:
        "View and update a user's profile. The header displays their name, email, last sign-in, and when the account was created.",
      side: 'bottom' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="user-detail-basic-info"]',
    popover: {
      title: 'Basic Information',
      description:
        "Update the user's first name, last name, and email here. The email is used for login and notifications.",
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="user-detail-roles"]',
    popover: {
      title: 'Access Type',
      description:
        'Choose an access type for this user by selecting one of the access cards below.',
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="user-detail-role-monitoring"]',
    popover: {
      title: 'Monitoring Access',
      description:
        'Monitoring Access lets the user work with alerts and monitoring only. They cannot change customers, sites, devices, or system settings.',
      side: 'top' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="user-detail-role-admin"]',
    popover: {
      title: 'Admin Access',
      description:
        'Admin Access lets the user manage customers, sites, devices, connections, users, teams, and settings, but not monitoring alerts.',
      side: 'top' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="user-detail-role-full"]',
    popover: {
      title: 'Full Access',
      description:
        'Full Access gives both admin and monitoring permissions, so the user can manage configuration and handle alerts end to end.',
      side: 'top' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="user-detail-save-button"]',
    popover: {
      title: 'Save Changes',
      description:
        'When you change user details, a save banner appears. Click it to save all updates.',
      side: 'top' as const,
      align: 'end' as const,
    },
  },
];

export const userRoutes: RouteConfig[] = [
  {
    id: 'users',
    processName: ONBOARDING_KEYS.users,
    match: (path: string) => path === '/users',
    steps: usersPageSteps,
  },
  {
    id: 'add-user',
    processName: ONBOARDING_KEYS.addUser,
    match: (path: string) => path === '/users/add',
    steps: addUserSteps,
  },
  {
    id: 'user-detail',
    processName: ONBOARDING_KEYS.userDetail,
    match: (path: string) => /^\/users\/[^/]+$/.test(path) && path !== '/users/add',
    steps: userDetailSteps,
  },
];
